import { Injectable, Injector } from '@angular/core';
import type { Observable } from 'rxjs';
import type { ActionDispatcher } from './action-dispatcher';
import { ACTION_HANDLERS, ActionHandlerDispatcher } from './action-handler';
import type { ActionRequest } from './action-request';
import type { ActionEvent } from './action-response';
import { FallbackActionDispatcher } from './fallback-action-dispatcher';

/**
 * `ActionDispatcher` that dispatches `ActionRequests` to a chain
 * of `ActionHandler`s.
 *
 * This is the implementation of the `ActionDispatcher` that will be
 * injected into the `ActionService` via dependency injection.
 */
@Injectable()
export class ActionHandlingDispatcher implements ActionDispatcher {
  private _chain: ActionDispatcher | null = null;

  /**
   * Chain of `ActionHandlerDispatcher`s
   */
  private get chain(): ActionDispatcher {
    if (this._chain === null) {
      this._chain = this._initializeChain();
    }

    return this._chain;
  }

  constructor(
    /**
     * Fallback dispatcher
     */
    private fallback: FallbackActionDispatcher,
    private injector: Injector,
  ) {}

  dispatch(req: ActionRequest<any>): Observable<ActionEvent<any>> {
    // Dispatch request through chain
    return this.chain.dispatch(req);
  }

  isExecuteable(action: string): Observable<boolean> {
    // Dispatch request through chain
    return this.chain.isExecuteable(action);
  }

  private _initializeChain() {
    // Initialize chain, putting the fallback at latest
    return this.injector
      .get(ACTION_HANDLERS, [])
      .reduceRight<ActionDispatcher>(
        (next, handler) => new ActionHandlerDispatcher(next, handler),
        this.fallback,
      );
  }
}
